import React, { useState, useEffect } from 'react';
import styles from './Testimonials.module.css';
import { FaArrowLeft, FaUpload, FaTrash } from 'react-icons/fa';

interface Testimonial {
  testimonialsId: number;
  userImageUrl: string;
  [key: string]: any;
}

interface TestimonialsAdminProps {
  onBack?: () => void;
}

const TestimonialsAdmin: React.FC<TestimonialsAdminProps> = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(false);

  // Fetch testimonials on component mount
  useEffect(() => {
    fetchTestimonials();
  }, []);

  // GET - Fetch all testimonials
  const fetchTestimonials = async () => {
    try {
      setLoadingTestimonials(true);
      const response = await fetch('https://fhome-web-api-dev-cpefddczabfqcmhn.canadacentral-01.azurewebsites.net/Testimonials/GetTestimonials', {
        method: 'GET',
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Testimonials data:', data); // Debug log to see the structure
        setTestimonials(data);
      } else {
        setMessage("Failed to fetch testimonials");
      }
    } catch (error) {
      setMessage("Error fetching testimonials");
      console.error(error);
    } finally {
      setLoadingTestimonials(false);
    }
  };

  // DELETE - Delete a testimonial by ID
  const handleDeleteTestimonial = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this testimonial?")) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`https://fhome-web-api-dev-cpefddczabfqcmhn.canadacentral-01.azurewebsites.net/Testimonials/DeleteTestimonial/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setMessage("Testimonial deleted successfully ✓");
        setTestimonials(testimonials.filter(t => t.testimonialsId !== id));
      } else {
        setMessage("Failed to delete testimonial");
      }
    } catch (error) {
      setMessage("Error deleting testimonial");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!imageFile) {
      setMessage("Please select an image");
      return;
    }

    try {
      setLoading(true);
      setMessage("Uploading image...");

      const formData = new FormData();
      formData.append("image", imageFile);

      const response = await fetch('https://fhome-web-api-dev-cpefddczabfqcmhn.canadacentral-01.azurewebsites.net/Testimonials/CreateTestimonial', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setMessage("Image uploaded successfully ✓");
        setImageFile(null);
      } else {
        setMessage("Upload failed");
      }
    } catch (error) {
      setMessage("Error uploading image");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.adminContainer}>
      <div className={styles.adminCard}>
        {onBack && (
          <button onClick={onBack} className={styles.backBtn}>
            <FaArrowLeft /> Back
          </button>
        )}

        <h2 className={styles.adminTitle}>Upload Testimonial Image</h2>

        {message && <div className={styles.message}>{message}</div>}

        <div className={styles.adminSection}>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            disabled={loading}
          />

          <button
            onClick={handleUpload}
            disabled={loading}
            className={styles.adminButtonPrimary}
          >
            <FaUpload /> Upload Image
          </button>
        </div>

        {/* Display existing testimonials */}
        <div className={styles.adminSection}>
          <h3>Existing Testimonials</h3>
          {loadingTestimonials ? (
            <p>Loading testimonials...</p>
          ) : testimonials.length === 0 ? (
            <p>No testimonials found</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
              {testimonials.map((testimonial) => {
                // Construct the image URL safely
                let imageUrl = '';
                if (testimonial.userImageUrl) {
                  imageUrl = testimonial.userImageUrl.startsWith('http') 
                    ? testimonial.userImageUrl 
                    : `https://fhome-web-api-dev-cpefddczabfqcmhn.canadacentral-01.azurewebsites.net${testimonial.userImageUrl}`;
                }
                
                return (
                  <div key={testimonial.testimonialsId} style={{ border: '1px solid #ccc', padding: '10px', borderRadius: '5px', position: 'relative' }}>
                    <img
                      src={imageUrl || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="150"%3E%3Crect fill="%23f0f0f0" width="150" height="150"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="14" fill="%23999"%3ENo image%3C/text%3E%3C/svg%3E'}
                      alt="Testimonial"
                      style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '3px' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="150"%3E%3Crect fill="%23f0f0f0" width="150" height="150"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="12" fill="%23999"%3EFailed to load%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    <button
                      onClick={() => handleDeleteTestimonial(testimonial.testimonialsId)}
                      disabled={loading}
                      style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        background: '#ff4444',
                        color: 'white',
                        border: 'none',
                        padding: '5px 8px',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestimonialsAdmin;

